import { darken } from '@mui/material/styles'
import { ChevronLeftIcon, ClearIcon, ToolsIcon } from '@weblite/icons'
import { alpha, IconButton, styled, Typography, useTheme } from '@mui/material'
import { WAPP_DEV_ENV_WAPP_ID } from 'weblite-web-wapprunner'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  FORM_CONFIG,
  POLL_CONFIG,
  NEVISEH_CONFIG,
  GENERAL_EXAM_CONFIG,
  WAPP_DEV_ENV_CONFIG,
} from '$common/wapp-bar-theme'
import {
  ChatListToolboxListItem,
  ChatListToolboxListItemVariant,
} from '$comp/chat-list-tool-box/components/list-item/list-item'
import { otherWappsConfig } from '$comp/chat-list-tool-box/components/other-wapps/other-wapps'
import { showChatListToolboxOtherWappsModal } from '$comp/modals/chat-list-toolbox/chat-list-toolbox-other-wapps-modal.api'
import { WappPanel } from '$logic/wapp-panels'
import {
  openFormCreator,
  openGeneralExamCreator,
  openNeviseCreator,
  openPollCreator,
} from '$logic/wapps/business'
import hostConf from '$config/frontend'
import { t } from '$i18n'

interface Props {
  onClose?: () => void
}

const wappsConfig = [
  {
    ...NEVISEH_CONFIG,
    borderColor: '#FA4E4E',
    backgroundColor: '#FFEFEF',
    variant: 'fullWidth',
    description: t('chatListToolbox.wappDescription.neviseh'),
    onClick: openNeviseCreator,
    logoGradient: 'linear-gradient(60deg, #F57073 13.4%, #FA4545 86.6%)',
    hoverColor: `${darken('#FFEFEF', 0.1)}`,
  },
  {
    ...GENERAL_EXAM_CONFIG,
    backgroundColor: '#E8F0FF',
    borderColor: '#3B80F0',
    variant: 'halfWidth',
    description: t('chatListToolbox.wappDescription.generalExam'),
    onClick: openGeneralExamCreator,
    logoGradient: 'linear-gradient(0deg, #284FE7 4.55%, #53BEFB 95.45%)',
    hoverColor: `${darken('#E8F0FF', 0.1)}`,
  },
  {
    ...FORM_CONFIG,
    backgroundColor: '#FFEBCA',
    borderColor: '#FB9D00',
    variant: 'halfWidth',
    description: t('chatListToolbox.wappDescription.form'),
    onClick: openFormCreator,
    logoGradient: 'linear-gradient(180deg, #FFE040 0%, #FF8C21 100%)',
    hoverColor: `${darken('#FFEBCA', 0.1)}`,
  },
  {
    ...POLL_CONFIG,
    backgroundColor: '#DDFFE9',
    borderColor: '#07A779',
    variant: 'fullWidth',
    description: t('chatListToolbox.wappDescription.poll'),
    onClick: openPollCreator,
    logoGradient: 'linear-gradient(180deg, #0ACD58 0%, #07A57B 100%)',
    hoverColor: `${darken('#DDFFE9', 0.1)}`,
  },
  hostConf.HAS_WAPP_DEV_ENV && {
    ...WAPP_DEV_ENV_CONFIG,
    backgroundColor: '#E8F0FF',
    borderColor: '#3B80F0',
    variant: 'fullWidth',
    description: t('chatListToolbox.wappDescription.wappDevEnv'),
    onClick: () => WappPanel.openWapp({ wappId: WAPP_DEV_ENV_WAPP_ID }),
    logoGradient: 'linear-gradient(0deg, #284FE7 4.55%, #53BEFB 95.45%)',
    hoverColor: `${darken('#E8F0FF', 0.1)}`,
  },
].filter(Boolean)

const otherWappsNames = otherWappsConfig.reduce(
  (prev, config, index, array) => {
    const isLast = index === array.length - 1

    // TODO[toolbox]: Use i18n
    const delimiter = !prev ? '' : isLast ? ' و ' : '، '
    return `${prev}${delimiter}${config.name}`
  },
  '',
)

export function ChatListToolBoxHome({ onClose }: Props) {
  const theme = useTheme()

  return (
    <Root>
      <Header>
        <Typography variant="h3" color="inherit">
          {t('chatListToolbox.title')}
        </Typography>
        <IconButton color="inherit" onClick={onClose}>
          <ClearIcon />
        </IconButton>
      </Header>
      <WappsGrid>
        {wappsConfig.map(wappConfig => (
          <ChatListToolboxListItem
            icon={wappConfig.icon}
            title={wappConfig.name}
            description={wappConfig.description}
            borderColor={wappConfig.borderColor}
            backgroundColor={wappConfig.backgroundColor}
            onClick={async () => {
              await closeCurrentModal()
              wappConfig.onClick()
            }}
            variant={wappConfig.variant as ChatListToolboxListItemVariant}
            gradient={wappConfig.logoGradient}
            hoverColor={wappConfig.hoverColor}
          />
        ))}

        <ChatListToolboxListItem
          icon={<ToolsIcon />}
          title={t('chatListToolbox.otherWapps')}
          description={otherWappsNames}
          borderColor={theme.palette.neutral500}
          backgroundColor={theme.palette.neutral000}
          onClick={async () => {
            await closeCurrentModal()
            showChatListToolboxOtherWappsModal()
          }}
          variant="fullWidth"
          gradient={theme.palette.neutral500}
          endIconButton={
            <IconButton color="inherit">
              <ChevronLeftIcon />
            </IconButton>
          }
          hoverColor={darken(theme.palette.neutral000, 0.1)}
        />
      </WappsGrid>

      {/*<CulturalContents onClick={() => showToolboxCulturalContentsModal()} />*/}
      {/*<Stack*/}
      {/*  sx={{ width: '100%' }}*/}
      {/*  direction="row"*/}
      {/*  justifyContent="space-between"*/}
      {/*>*/}
      {/*  <WritingTools />*/}
      {/*  <UsefulTools />*/}
      {/*</Stack>*/}
      {/*<ConnectivityTools onClick={() => showToolboxCommunicationToolsModal()} />*/}
      {/*<StyledIconButton onClick={onClose}>*/}
      {/*  <CloseIcon />*/}
      {/*</StyledIconButton>*/}
    </Root>
  )
}

const Root = styled('div')`
  padding: 12px;
  backdrop-filter: blur(3px);
  background-color: ${({ theme }) => alpha(theme.palette.neutral000, 0.7)};
`

const WappsGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 6px;
`

const Header = styled('div')`
  display: flex;
  height: 40px;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.neutral700};
  margin-bottom: 6px;
`
